/**
 * MKS-345 — the lead's stage trail: who moved it from which column to which,
 * and when.
 *
 * The backend writes one row per stage change inside the same transaction as
 * the move itself (`recordLeadStageChange`), including the row created with
 * the lead, so this list always has at least one entry for a lead that exists.
 */
import { useLeadStageHistory } from '../../hooks/useLeadStages';
import { formatDisplayDateTime } from '../../utils/format';

interface LeadStageTrailProps {
  leadId: number;
}

function personName(
  person: { firstName: string | null; lastName: string | null } | null,
): string {
  if (!person) return 'Monksheets';
  const name = [person.firstName, person.lastName].filter(Boolean).join(' ').trim();
  return name || 'Monksheets';
}

export default function LeadStageTrail({ leadId }: LeadStageTrailProps) {
  const { data, isLoading } = useLeadStageHistory(leadId);
  const entries = data?.data ?? [];

  return (
    <div className="col-span-2">
      <h3 className="mb-2 text-sm font-medium text-gray-700">Stage history</h3>
      {isLoading ? (
        <p className="text-xs text-gray-400">Loading…</p>
      ) : entries.length === 0 ? (
        <p className="text-xs text-gray-400">No stage changes recorded yet.</p>
      ) : (
        <ul className="max-h-40 space-y-1 overflow-y-auto" data-testid="lead-stage-trail">
          {entries.map((entry) => (
            <li key={entry.id} className="text-xs text-gray-600">
              <span className="font-medium text-gray-900">
                {/* A deleted stage leaves its history rows behind with a null
                    reference rather than erasing them (ON DELETE SET NULL). */}
                {entry.fromStage ? entry.fromStage.name : 'Created'} →{' '}
                {entry.toStage ? entry.toStage.name : 'a deleted stage'}
              </span>{' '}
              <span className="text-gray-400">
                by {personName(entry.changedBy)} · {formatDisplayDateTime(entry.createdAt)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
